import {getPointFeature, metersToPixelsAtMaxZoom} from './map-utils';
import MapboxFilter from './mapbox-filter';
import Mediator from 'common/scripts/mediator';

const mediator = new Mediator();
const Mapboxgl = window.mapboxgl;

import tmplTooltip from './mapbox-tooltip.twig';
import tmplTooltipIcon from './mapbox-tooltip-icon.twig';
import Utils from 'common/scripts/utils';

const iconsData = [{
    count: 0,
    src  : '/icons/mapbox/media/pharmacy.svg',
    type : 'pharmacy',
    name : 'Аптеки'
}, {
    count: 0,
    src  : '/icons/mapbox/media/kindergarden.svg',
    type : 'kindergarden',
    name : 'Детские сады'
}, {
    count: 0,
    src  : '/icons/mapbox/media/school.svg',
    type : 'school',
    name : 'Школы'
}, {
    count: 0,
    src  : '/icons/mapbox/media/shop.svg',
    type : 'shop',
    name : 'Магазины'
}, {
    count: 0,
    src  : '/icons/mapbox/media/park.svg',
    type : 'park',
    name : 'Парки'
}, {
    count: 0,
    src  : '/icons/mapbox/media/shopping-center.svg',
    type : 'shopping-center',
    name : 'Торговые центры'
}, {
    count: 0,
    src  : '/icons/mapbox/media/cafe.svg',
    type : 'cafe',
    name : 'Кафе'
}];

export default class Mapbox {
    constructor() {
        this.map = {};
        this.data = {};
        this.tooltip = {};
        this.markerElems = [];
        this.iconElems = [];
        this.staticMarkers = [];
        this.activeClass = 'is-active';
        this.wrapper = null;
        this.url = null;
        this.json = null;
        this.hasTooltips = null;
        this.filter = null;
        this.isTouch = Utils.isTouch();
        this.isDinamic = null;
    }

    init(element) {
        this.wrapper = element;
        this.url = this.wrapper.dataset.ajax;
        this.json = this.wrapper.dataset.json;
        this.hasTooltips = this.wrapper.dataset.tooltip;
        this.isDinamic = this.wrapper.dataset.dinamic;
        this.zoomIn = this.wrapper.querySelector('.j-mapbox-zoom-in');
        this.zoomOut = this.wrapper.querySelector('.j-mapbox-zoom-out');
        this.createMap();

        if (this.url) {
            this._getDataFetch();
        }

        mediator.subscribe('tabSwitched', () => {
            this.map.resize();
        });

        if (this.json) {
            const decode = JSON.parse(decodeURIComponent(escape(atob(this.json))));

            this.data = decode.data;
            this._setMapSettings(this.data);
        }
        this._bindEvents();
    }

    _bindEvents() {
        if (this.zoomIn) {
            this.zoomIn.addEventListener('click', () => {
                this.map.zoomIn();
            });
        }

        if (this.zoomOut) {
            this.zoomOut.addEventListener('click', () => {
                this.map.zoomOut();
            });
        }
    }

    createMap() {
        if (!Mapboxgl) {
            return;
        }

        Mapboxgl.accessToken =
            'pk.eyJ1Ijoic2FtdWVsNzEzIiwiYSI6ImNrcHBqNnl5aDBueXUycG1uNGp2MGJwc2wifQ.cX_qStxHELOtBYnH2ABRdA';

        this.map = new Mapboxgl.Map({
            container: this.wrapper,
            style    : 'mapbox://styles/mapbox/streets-v11',
            zoom     : 13,
            center   : [30.731474, 46.422306]
        });
    }

    _getDataFetch() {
        fetch(this.url)
            .then((data) => {
                return data.json();
            })
            .then((res) => {
                this.data = res.data;

                this._setMapSettings(this.data);
            })
			.catch((error) => { // eslint-disable-line
				return console.error(error); // eslint-disable-line
            });
    }

    _setMapSettings(data) {
        this.map.setCenter(data.center);
        this.map.setZoom(data.zoom);
        this._setMapBehaviour(data);

        if (data.circles && data.circles.length) {
            this._addCircles(data.circles);
        }
        if (data.icons && data.icons.length) {
            this._addIcons(data.icons);
            this._initFilter(data.icons);
        }

        if (data.markers.length) {
            if (this.isDinamic) {
                this.addMarkers(data.markers);
            } else {
                this._addStaticMarkers(data.markers);
            }
        }
        this.setMapReady();
    }

    setMapReady() {
        mediator.publish('mapReady', this.map);
    }

    _setMapBehaviour(data) {
        if (data.scrollZoom === false) {
            this.map.scrollZoom.disable();
        }
    }

    _createTooltip(offset = 60) {
        return new Mapboxgl.Popup({
            closeButton: false,
            offset
        });
    }

    addMarkers(markers) {
        markers.forEach((marker) => {
            const elem = document.createElement('div');

            elem.classList.add('mapbox__marker', 'j-marker');
            elem.setAttribute('data-id', marker.id);
            elem.style.backgroundImage = `url(${marker.img})`;

            const markerObject = new Mapboxgl.Marker(elem)
                .setLngLat(marker.coords);

            if (this.hasTooltips) {
                this.tooltip = this._createTooltip().setHTML(tmplTooltip(marker));
                markerObject.setPopup(this.tooltip)
                    .addTo(this.map);
                this._bindMarkerHoverEvents(markerObject);
            } else {
                markerObject.addTo(this.map);
            }

            this.markerElems.push(elem);
            mediator.publish('markersReady', this.map, this.markerElems);
        });
    }

    _bindMarkerHoverEvents(markerObject) {
        const markerDiv = markerObject.getElement();

        markerDiv.addEventListener('mouseenter', () => {
            return markerDiv.classList.add(this.activeClass);
        });
        markerDiv.addEventListener('mouseleave', () => {
            return markerDiv.classList.remove(this.activeClass);
        });
    }

    _addCircles(circles) {
        circles.forEach((circle, index) => {
            this.map.on('load', () => {
                const dataLabel = getPointFeature(circle.center, circle.radius, 0);

                dataLabel.properties.title = circle.title;

                this.map.addLayer({
                    id   : `circle-${index}`,
                    type : 'circle',
                    paint: {
                        'circle-radius': {
                            stops: [[0, 0], [20, metersToPixelsAtMaxZoom(circle.radius * 1000, circle.center[1])]],
                            base : 2
                        },
                        'circle-color'       : 'transparent',
                        'circle-stroke-color': '#F77737',
                        'circle-stroke-width': 1
                    },
                    source: {
                        type: 'geojson',
                        data: {
                            type    : 'FeatureCollection',
                            features: [{
                                type    : 'Feature',
                                geometry: {
                                    type       : 'Point',
                                    coordinates: circle.center
                                }
                            }]
                        }
                    }
                });

                this.map.addLayer({
                    id    : `label-${index}`,
                    type  : 'symbol',
                    source: {
                        type: 'geojson',
                        data: dataLabel
                    },
                    layout: {
                        'text-field' : ['get', 'title'],
                        'text-size'  : 12,
                        'text-offset': [0, 2]
                    },
                    paint: {'text-color': '#F77737'}
                });
            });
        });
    }

    _addIcons(icons) {
        icons.forEach((icon) => {
            const iconData = iconsData.find((item) => {
                return item.type === icon.type;
            });
            const elem = document.createElement('div');

            elem.classList.add('mapbox__icon', 'j-icon');
            elem.setAttribute('data-id', icon.id);
            elem.setAttribute('data-type', icon.type);
            elem.style.backgroundImage = `url(${iconData.src})`;

            this.tooltip = this._createTooltip(20);

            const iconObject = new Mapboxgl.Marker(elem)
                .setLngLat(icon.coords);

            if (this.isTouch) {
                iconObject.setPopup(this.tooltip.setHTML(tmplTooltipIcon(icon)))
                    .addTo(this.map);
            } else {
                this._bindIconHoverEvents(elem, icon);
                iconObject.addTo(this.map);
            }
            this.iconElems.push(elem);
        });
    }

    _bindIconHoverEvents(elem, icon) {
        elem.addEventListener('mouseenter', () => {
            this.tooltip
                .setLngLat(icon.coords)
                .setHTML(tmplTooltipIcon(icon))
                .addTo(this.map);
        });
        elem.addEventListener('mouseleave', () => {
            this.tooltip.remove();
        });
    }

    _initFilter(data) {
        this.filter = new MapboxFilter(this.wrapper, this.iconElems);
        this.filter.init(data, iconsData);
    }

    _addStaticMarkers(markers) {
        markers.forEach((marker, index) => {
            const elem = document.createElement('div');
            const modify = marker.objectType ? `marker_theme_${marker.objectType}` : '';

            elem.classList.add('marker', 'j-map-marker', !index && this.activeClass, modify);
            elem.setAttribute('data-id', marker.id || index);
            if (marker.icon) {
                elem.style.backgroundImage = `url(${marker.icon})`;
            }
            if (marker.color) {
                elem.style.backgroundColor = marker.color;
            }

            this.staticMarkers.push(new Mapboxgl.Marker(elem)
                .setLngLat(marker.coords)
                .addTo(this.map));
            this.markerElems.push(elem);
        });
        this._bindMarkersEvents();
        mediator.publish('markersReady', this.map, this.markerElems);
    }

    _bindMarkersEvents() {
        this.markerElems.forEach((item) => {
            item.addEventListener('click', () => {
                this._setMarkerActive(item);
                mediator.publish('markerClick', item);
            });
        });
    }

    _setMarkerActive(item) {
        if (!item.classList.contains(this.activeClass)) {
            this.markerElems.forEach((marker) => {
                marker.classList.remove(this.activeClass);
            });
            item.classList.add(this.activeClass);
        }
    }
}
